import { useState, useCallback } from 'react';
import { useDocumentStore } from '../../stores/documentStore';
import { PDFManipulator } from '../../core/PDFManipulator';
import { pdfEngine } from '../../core/PDFEngine';
import { readFileAsArrayBuffer, downloadUint8Array, downloadBlob } from '../../utils/fileUtils';
import { useUIStore } from '../../stores/uiStore';
import './Dialogs.css';

interface ConvertDialogProps {
  onClose: () => void;
}

type ConvertMode = 'image-to-pdf' | 'pdf-to-image' | 'pdf-to-text';

export function ConvertDialog({ onClose }: ConvertDialogProps) {
  const { documents, activeDocumentId, totalPages } = useDocumentStore();
  const activeDoc = documents.find((d) => d.id === activeDocumentId);
  const [mode, setMode] = useState<ConvertMode>('image-to-pdf');
  const [imageFiles, setImageFiles] = useState<File[]>([]);
  const [isConverting, setIsConverting] = useState(false);
  const [imageFormat, setImageFormat] = useState<'png' | 'jpeg'>('png');

  const handleImageFiles = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files) {
      setImageFiles(Array.from(files).filter((f) => f.type.startsWith('image/')));
    }
  }, []);

  const handleConvert = useCallback(async () => {
    setIsConverting(true);

    try {
      if (mode === 'image-to-pdf') {
        if (imageFiles.length === 0) {
          useUIStore.getState().addToast({ type: 'warning', message: 'Please select images first' });
          return;
        }

        const images = await Promise.all(
          imageFiles.map(async (file) => ({
            data: await readFileAsArrayBuffer(file),
            type: (file.type.includes('png') ? 'png' : 'jpg') as 'png' | 'jpg',
          }))
        );

        const result = await PDFManipulator.createFromImages(images);
        downloadUint8Array(result, 'converted.pdf');
        useUIStore.getState().addToast({ type: 'success', message: 'Images converted to PDF!' });
      } else if (mode === 'pdf-to-image') {
        if (!activeDoc?.arrayBuffer) return;

        // Render each page as image using canvas
        for (let i = 1; i <= totalPages; i++) {
          const canvas = document.createElement('canvas');
          await pdfEngine.renderPage(i, { scale: 2, rotation: 0, canvas });

          canvas.toBlob(
            (blob) => {
              if (blob) {
                downloadBlob(blob, `page_${i}.${imageFormat}`);
              }
            },
            `image/${imageFormat}`,
            0.95
          );
        }
        useUIStore.getState().addToast({ type: 'success', message: `Exported ${totalPages} pages as images!` });
      } else if (mode === 'pdf-to-text') {
        const text = await pdfEngine.extractFullText();
        const blob = new Blob([text], { type: 'text/plain' });
        downloadBlob(blob, `${activeDoc?.filename.replace('.pdf', '')}_text.txt`);
        useUIStore.getState().addToast({ type: 'success', message: 'Text extracted!' });
      }

      onClose();
    } catch (err) {
      console.error('Convert failed:', err);
      useUIStore.getState().addToast({ type: 'error', message: 'Conversion failed' });
    } finally {
      setIsConverting(false);
    }
  }, [mode, imageFiles, activeDoc, totalPages, imageFormat, onClose]);

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()} style={{ width: 480 }}>
        <div className="modal-header">
          <h3>🔄 Convert</h3>
          <button className="btn btn-ghost btn-icon" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          <div className="convert-modes">
            <button className={`convert-mode-btn ${mode === 'image-to-pdf' ? 'active' : ''}`} onClick={() => setMode('image-to-pdf')}>
              <span>🖼️ → 📄</span>
              <span>Images to PDF</span>
            </button>
            <button className={`convert-mode-btn ${mode === 'pdf-to-image' ? 'active' : ''}`} onClick={() => setMode('pdf-to-image')} disabled={!activeDoc}>
              <span>📄 → 🖼️</span>
              <span>PDF to Images</span>
            </button>
            <button className={`convert-mode-btn ${mode === 'pdf-to-text' ? 'active' : ''}`} onClick={() => setMode('pdf-to-text')} disabled={!activeDoc}>
              <span>📄 → 📝</span>
              <span>PDF to Text</span>
            </button>
          </div>

          {mode === 'image-to-pdf' && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <label className="file-drop-zone">
                <input type="file" accept="image/*" multiple onChange={handleImageFiles} className="sr-only" />
                <span className="file-drop-icon">🖼️</span>
                <span className="file-drop-label">Select images (PNG, JPG, WebP)</span>
              </label>
              {imageFiles.length > 0 && (
                <p className="dialog-info" style={{ marginTop: 'var(--space-2)' }}>
                  {imageFiles.length} image(s) selected
                </p>
              )}
            </div>
          )}

          {mode === 'pdf-to-image' && (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <div className="input-group">
                <label>Output format</label>
                <select className="input" value={imageFormat} onChange={(e) => setImageFormat(e.target.value as 'png' | 'jpeg')}>
                  <option value="png">PNG (Lossless)</option>
                  <option value="jpeg">JPEG (Smaller size)</option>
                </select>
              </div>
              <p className="dialog-info" style={{ marginTop: 'var(--space-2)' }}>
                Will export {totalPages} page(s) as individual images
              </p>
            </div>
          )}

          {mode === 'pdf-to-text' && (
            <p className="dialog-info" style={{ marginTop: 'var(--space-4)' }}>
              Extract all text content from the PDF into a plain text file. For scanned PDFs, use OCR instead.
            </p>
          )}
        </div>

        <div className="modal-footer">
          <button className="btn btn-secondary" onClick={onClose}>Cancel</button>
          <button className="btn btn-primary" onClick={handleConvert} disabled={isConverting}>
            {isConverting ? '⏳ Converting...' : 'Convert'}
          </button>
        </div>
      </div>
    </div>
  );
}
